import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    User,
    Phone,
    MapPin,
    Gauge,
    Users,
    MessageSquareText,
    CheckCircle2,
    Loader2,
    MessageCircle,
} from "lucide-react";
import { PageHeader, FadeUp } from "../components/Reveal";
import { BRAND, PLANS, waLink } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_OPTIONS = [...PLANS.map((p) => `${p.speed} Mbps`), "Not Sure"];

const inputCls =
    "w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3.5 pl-11 text-sm text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-200 focus:border-[#E61C24] focus:shadow-[0_0_0_3px_rgba(230,28,36,0.2)]";

const Field = ({ icon: Icon, label, htmlFor, children }) => (
    <div>
        <label
            htmlFor={htmlFor}
            className="mb-2 block text-sm font-semibold text-slate-300"
        >
            {label}
        </label>
        <div className="relative">
            <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            {children}
        </div>
    </div>
);

export default function GetConnected() {
    const [params] = useSearchParams();
    const preselect = params.get("plan");
    const initialPlan = PLAN_OPTIONS.includes(`${preselect} Mbps`)
        ? `${preselect} Mbps`
        : "";

    const [form, setForm] = useState({
        name: "",
        mobile: "",
        location: "",
        plan: initialPlan,
        customer_type: "New Connection",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.plan) {
            toast.error("Please choose a preferred plan");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setSubmitted(true);
            const text = [
                "New Connection Enquiry — Eway Fiber Net",
                `Name: ${form.name}`,
                `Mobile: ${form.mobile}`,
                `Location: ${form.location}`,
                `Preferred Plan: ${form.plan}`,
                `Type: ${form.customer_type}`,
                form.message ? `Message: ${form.message}` : null,
            ]
                .filter(Boolean)
                .join("\n");
            window.open(waLink(text), "_blank", "noopener");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Could not submit right now. Please call or WhatsApp us instead."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageHeader
                testId="get-connected-header"
                eyebrow="NEW CONNECTION"
                title={
                    <>
                        Get Your <span className="text-gradient-fire">Eway Connection</span>
                    </>
                }
                subtitle="Fill in your details and our team will reach out to set up your new FTTH connection."
            />

            <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-6 lg:px-8">
                <FadeUp>
                    {submitted ? (
                        <div
                            data-testid="enquiry-success-message"
                            className="glass rounded-3xl p-10 text-center"
                        >
                            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-400">
                                <CheckCircle2 className="h-9 w-9" />
                            </span>
                            <h2 className="font-display mt-6 text-2xl font-extrabold text-white sm:text-3xl">
                                Thank you!
                            </h2>
                            <p className="mx-auto mt-3 max-w-md text-base text-slate-300">
                                Our Eway Fiber Net team will contact you shortly.
                            </p>
                            <p className="mt-2 flex items-center justify-center gap-2 text-sm text-slate-400">
                                <MessageCircle className="h-4 w-4 text-[#1FAF54]" />
                                Your details were also opened in WhatsApp for instant
                                confirmation.
                            </p>
                            <button
                                type="button"
                                data-testid="enquiry-again-button"
                                onClick={() => {
                                    setSubmitted(false);
                                    setForm({
                                        name: "",
                                        mobile: "",
                                        location: "",
                                        plan: "",
                                        customer_type: "New Connection",
                                        message: "",
                                    });
                                }}
                                className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-[border-color,color] duration-200 hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                            >
                                Send Another Enquiry
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            data-testid="enquiry-form"
                            className="glass space-y-6 rounded-3xl p-6 sm:p-10"
                        >
                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field icon={User} label="Name" htmlFor="enquiry-name">
                                    <input
                                        id="enquiry-name"
                                        data-testid="enquiry-name-input"
                                        type="text"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your full name"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field icon={Phone} label="Mobile Number" htmlFor="enquiry-mobile">
                                    <input
                                        id="enquiry-mobile"
                                        data-testid="enquiry-mobile-input"
                                        type="tel"
                                        required
                                        inputMode="numeric"
                                        pattern="[0-9+ -]{10,15}"
                                        value={form.mobile}
                                        onChange={set("mobile")}
                                        placeholder="10-digit mobile number"
                                        className={inputCls}
                                    />
                                </Field>
                            </div>

                            <Field icon={MapPin} label="Location / Area" htmlFor="enquiry-location">
                                <input
                                    id="enquiry-location"
                                    data-testid="enquiry-location-input"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={form.location}
                                    onChange={set("location")}
                                    placeholder="Your area in Narasaraopet"
                                    className={inputCls}
                                />
                            </Field>

                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field icon={Gauge} label="Preferred Plan" htmlFor="enquiry-plan">
                                    <select
                                        id="enquiry-plan"
                                        data-testid="enquiry-plan-select"
                                        required
                                        value={form.plan}
                                        onChange={set("plan")}
                                        className={`${inputCls} appearance-none bg-[#131927]`}
                                    >
                                        <option value="" disabled>
                                            Select a plan
                                        </option>
                                        {PLAN_OPTIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field icon={Users} label="Connection Type" htmlFor="enquiry-type">
                                    <select
                                        id="enquiry-type"
                                        data-testid="enquiry-type-select"
                                        value={form.customer_type}
                                        onChange={set("customer_type")}
                                        className={`${inputCls} appearance-none bg-[#131927]`}
                                    >
                                        <option value="New Connection">New Connection</option>
                                        <option value="Existing Customer">Existing Customer</option>
                                    </select>
                                </Field>
                            </div>

                            <Field icon={MessageSquareText} label="Message" htmlFor="enquiry-message">
                                <textarea
                                    id="enquiry-message"
                                    data-testid="enquiry-message-input"
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Anything we should know? (optional)"
                                    className={`${inputCls} resize-none`}
                                />
                            </Field>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="enquiry-submit-button"
                                className="flex w-full items-center justify-center gap-3 rounded-full bg-[#E61C24] px-8 py-4 text-base font-extrabold text-white shadow-[0_10px_44px_rgba(230,28,36,0.5)] transition-[transform,background-color,opacity] duration-200 hover:scale-[1.02] hover:bg-[#C7151C] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    "Request Connection"
                                )}
                            </button>

                            <p className="text-center text-xs leading-relaxed text-slate-500">
                                Prefer to talk? Call{" "}
                                <a
                                    href={BRAND.phones[0].tel}
                                    className="font-semibold text-[#ff6b70] hover:text-white"
                                >
                                    {BRAND.phones[0].label}
                                </a>{" "}
                                ({BRAND.careHoursShort}) or{" "}
                                <Link to="/plans" className="font-semibold text-[#00F0FF] hover:text-white">
                                    browse plans
                                </Link>
                                .
                            </p>
                        </form>
                    )}
                </FadeUp>
            </section>
        </>
    );
}
